import styled from "@emotion/styled";
import CheckIcon from "@mui/icons-material/Check";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import React from "react";
import { normalizeValidationMessages } from "../../common/Logic/ServerResponse";
import { useAppDispatch } from "../../app/hooks";
import {
  deleteDocumentThunk,
  uploadSingleDocumentThunk,
} from "../../features/applicant/ApplicantSlice";
import { DocumentsViewDto } from "../../features/applicant/Models";
import SimpleBackdrop from "../loaders/backdrop";

interface Props {
  document: DocumentsViewDto;
}

const VisuallyHiddenInput = styled("input")({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DocumentContainer = styled(Box)(({ theme }) => ({
  padding: "16px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 2px",
  borderRadius: "4px",
  position: "relative",
  minHeight: "120px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  borderLeft: "3.5px solid green",
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  // height: "max-content",
}));

const UploadButton = styled(Button)<ButtonProps>(({ theme }) => ({
  border: "2px dotted #afafaf",
  width: "100%",
  height: "152px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textTransform: "none",
  '@media (hover: none)': {
    '&:active': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    }}
}));


// Hard cap for individual document uploads. Mirrors the rule shown on the
// upload tile ("Maximum upload file 4MB") and is also enforced server-side
// in DocumentsService.UploadSingleDocument. 4 * 1024 * 1024 = 4 MiB.
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
const PDF_MIME = "application/pdf";
/** Browsers often leave `file.type` empty or use octet-stream for PDFs (Windows / some scanners). */
const ALLOWED_FILE_TYPES = [PDF_MIME, "application/x-pdf", "application/acrobat", "application/vnd.pdf", "text/pdf", "text/x-pdf"];

const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

function fileLooksLikePdf(file: File): boolean {
  const lower = file.name.toLowerCase();
  return lower.endsWith(".pdf");
}

function resolvePdfMimeType(file: File): string | null {
  if (ALLOWED_FILE_TYPES.includes(file.type)) return PDF_MIME;
  if (!file.type && fileLooksLikePdf(file)) return PDF_MIME;
  if (file.type === "application/octet-stream" && fileLooksLikePdf(file)) {
    return PDF_MIME;
  }
  return null;
}

export default function UploadSingleFile({ document }: Props) {
  const dispatch = useAppDispatch();
  const [load, setLoad] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string>("");

  const getBase64 = (file: FileList) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file[0]);
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        reject(new Error("Could not read the file. Please try again."));
      };
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const myFile = e.target.files;
    if (!myFile || myFile.length === 0) return;

    setUploadError("");
    const file = myFile[0];

    const effectiveMime = resolvePdfMimeType(file);
    if (!effectiveMime) {
      setUploadError(
        "File type not supported. Please upload a .pdf file (max 4 MB)."
      );
      e.target.value = "";
      return;
    }

    // Pre-flight size check. We reject up-front so the user gets immediate
    // feedback instead of waiting for a server-side rejection round-trip.
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(
        `This file is ${formatBytes(
          file.size
        )} which is over the 4 MB limit. Please upload a smaller PDF.`
      );
      e.target.value = "";
      return;
    }

    setLoad(true);
    void getBase64(myFile)
      .then(async (base64) => {
        // Opt-in: set localStorage "debugUpload" to "1" then reload — breaks
        // here in dev so Sources breakpoints on minified bundles are not required.
        if (
          process.env.NODE_ENV === "development" &&
          typeof window !== "undefined" &&
          window.localStorage.getItem("debugUpload") === "1"
        ) {
          // eslint-disable-next-line no-debugger
          debugger;
        }

        const documentData = {
          ...document,
          base64String: base64,
          extension: effectiveMime,
          fileName: file.name,
          reference: document.reference ?? "",
        } as DocumentsViewDto;

        // Upload must not go through `formik.submitForm()`: the upload Formik
        // schema requires `documents.min(1)`. When that array is still empty or
        // stale, Yup fails silently and `onSubmit` never runs — looks like
        // "upload does nothing" with no server call.
        const action = await dispatch(uploadSingleDocumentThunk(documentData));
        const payload = action.payload as {
          validation?: { code?: number; messages?: unknown };
        } | null;
        const code = payload?.validation?.code;
        if (code !== 200 && code !== 201) {
          const msgs = normalizeValidationMessages(
            payload?.validation?.messages
          );
          setUploadError(
            msgs.length > 0
              ? msgs.join(" ")
              : "Upload failed. Please try again or use a different PDF."
          );
        }
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error ? err.message : "Upload failed. Please try again.";
        setUploadError(msg);
      })
      .finally(() => setLoad(false));
  };

  const handleDelete = () => {
    dispatch(deleteDocumentThunk(document.id));
  };

  const renderDocumentPreview = () => {
    if (!document || document.link === "") return null;

    return (
      <DocumentContainer>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {document.personalizedDocumentName}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
          >
            {document.fileName}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <CheckIcon fontSize="small" color="success" />
          <Typography variant="caption" color="text.secondary">
            Uploaded
          </Typography>
        </Box>

        <Box mt={2}>
          <Button
            size="small"
            onClick={handleDelete}
            variant="text"
            startIcon={<DeleteForeverIcon color="error" />}
            fullWidth
          >
            Delete or Replace
          </Button>
        </Box>

        {load && (
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1} mt={1}>
            <CircularProgress size={18} color="inherit" />
            <Typography variant="caption" color="text.secondary">
              {`Uploading the ${document.personalizedDocumentName || "document"}`}
            </Typography>
          </Box>
        )}
      </DocumentContainer>
    );
  };

  const renderUploadButton = () => {
    if (document?.link) return null;

    return (
      <Box>
        <UploadButton variant="text" component="label">
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <CloudUploadIcon fontSize="medium" />
            <Typography variant="body2">
              {document.personalizedDocumentName}
            </Typography>
            <Typography variant="caption" textAlign="center">
              Maximum upload file 4MB
              <br />
              Supported formats .pdf
            </Typography>
          </Box>
          <VisuallyHiddenInput
            id={`file-upload-${document.id}`}
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => handleFileUpload(e)}
          />
        </UploadButton>
        {uploadError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.5 }}
          >
            {uploadError}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Grid item xs={12} md={4} lg={3}>
      {document?.link ? renderDocumentPreview() : renderUploadButton()}
      <SimpleBackdrop
        status={load ? "loading" : ""}
        message={
          load
            ? `Uploading the ${document.personalizedDocumentName || "document"}`
            : undefined
        }
      />
    </Grid>
  );
}
